import React from "react";
import { Link } from "react-router-dom";
import { FiArrowUpRight } from "react-icons/fi";
import siteConfig from "../config/siteConfig";
import "../css/privacy.scss";
import "../css/returns.scss";

const returnSections = [
  ["eligibility", "Return eligibility"],
  ["exclusions", "What cannot be returned"],
  ["process", "Start a return"],
  ["refunds", "Refunds & exchanges"],
  ["issues", "Damage or wrong item"],
  ["international", "International returns"],
];

function Returns() {
  return (
    <main className="policy-page returns">
      <header className="policy-page__header">
        <p className="policy-page__eyebrow">SUPPORT / {siteConfig.wordmark}</p>
        <h1>RETURNS &amp;<br />EXCHANGES</h1>
        <p className="policy-page__lede">
          Changed your mind? Eligible unused gear can be returned within 30 days
          of delivery. Here is what to prepare and what happens next.
        </p>
        <div className="policy-page__meta">
          <span>POLICY UPDATED AUGUST 13, 2026</span>
          <span>NEED HELP? <Link to="/contact?subject=return">START A SUPPORT REQUEST</Link></span>
        </div>
      </header>

      <section className="policy-page__facts" aria-label="Return policy summary">
        <div>
          <span>01</span>
          <strong>30 DAYS</strong>
          <p>Request your return within 30 days after confirmed delivery.</p>
        </div>
        <div>
          <span>02</span>
          <strong>UNUSED GEAR</strong>
          <p>Items must be clean, complete, and in original sellable condition.</p>
        </div>
        <div>
          <span>03</span>
          <strong>ORIGINAL PAYMENT</strong>
          <p>Approved refunds return to the payment method used at checkout.</p>
        </div>
      </section>

      <div className="policy-page__layout">
        <aside className="policy-page__toc">
          <p>ON THIS PAGE</p>
          <nav aria-label="Return policy sections">
            {returnSections.map(([id, label], index) => (
              <a key={id} href={`#returns-${id}`}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                {label}
              </a>
            ))}
          </nav>
        </aside>

        <article className="policy-page__content">
          <div className="policy-page__intro">
            <p>
              Inspect your order when it arrives and keep the packaging until you
              are sure the gear is right for your setup. Return approval is based
              on the condition we receive, not only the date a request is opened.
            </p>
          </div>

          <section id="returns-eligibility" className="policy-page__section">
            <span className="policy-page__section-number">01</span>
            <h2>RETURN ELIGIBILITY</h2>
            <p>A standard return is eligible when all of the following are true:</p>
            <ul>
              <li>the request is submitted within 30 calendar days of confirmed delivery;</li>
              <li>the item was purchased directly from this online store;</li>
              <li>the product is unused, unmodified, and free from wear, odor, debris, or damage;</li>
              <li>all original parts, cables, manuals, protective films, gifts, and packaging are included; and</li>
              <li>proof of purchase and the order number can be provided.</li>
            </ul>
            <p>
              Bundles must be returned as a complete bundle unless support confirms
              otherwise. Products purchased from another retailer must be returned
              through that retailer under its policy.
            </p>
          </section>

          <section id="returns-exclusions" className="policy-page__section">
            <span className="policy-page__section-number">02</span>
            <h2>WHAT CANNOT BE RETURNED</h2>
            <p>The following are final sale unless required by law or confirmed defective:</p>
            <ul>
              <li>gift cards, downloadable products, and promotional items marked final sale;</li>
              <li>opened switch packs, installed keycaps, or consumable parts that show use;</li>
              <li>personalized, engraved, or otherwise customized products;</li>
              <li>products damaged by liquid, impact, incorrect installation, modification, or improper storage; and</li>
              <li>items missing serial labels, accessories, or substantial original packaging.</li>
            </ul>
            <div className="policy-page__note">
              <strong>OPENED IS NOT ALWAYS USED</strong>
              <p>
                Carefully opening a box to inspect an item does not automatically
                make it ineligible. The product and packaging must still be clean,
                complete, and suitable for resale.
              </p>
            </div>
          </section>

          <section id="returns-process" className="policy-page__section">
            <span className="policy-page__section-number">03</span>
            <h2>START A RETURN</h2>
            <ol className="returns__steps">
              <li>
                <span>01</span>
                <div><strong>OPEN A REQUEST</strong><p>Use the contact form and select “Return or exchange.” Include the email and order number used at checkout.</p></div>
              </li>
              <li>
                <span>02</span>
                <div><strong>WAIT FOR APPROVAL</strong><p>Support will confirm eligibility and send packing and routing instructions. Do not ship an unapproved return.</p></div>
              </li>
              <li>
                <span>03</span>
                <div><strong>PACK IT SECURELY</strong><p>Use the original product packaging inside a protective shipping carton. Remove or cover old carrier labels.</p></div>
              </li>
              <li>
                <span>04</span>
                <div><strong>KEEP THE RECEIPT</strong><p>Send the parcel by the deadline in the approval email and retain tracking until the return is complete.</p></div>
              </li>
            </ol>
            <Link className="site-button site-button--dark returns__action" to="/contact?subject=return">
              START A RETURN <FiArrowUpRight aria-hidden="true" />
            </Link>
          </section>

          <section id="returns-refunds" className="policy-page__section">
            <span className="policy-page__section-number">04</span>
            <h2>REFUNDS &amp; EXCHANGES</h2>
            <p>
              After a delivered return is inspected, we will email the result.
              Approved refunds are issued to the original payment method,
              typically within 5–10 business days after inspection. Your bank may
              take additional time to post the credit.
            </p>
            <p>
              Original delivery fees, customs charges, and return postage are not
              refundable unless the item arrived damaged, defective, or incorrect.
              If a return arrives incomplete, used, or damaged, we may reduce or
              decline the refund where permitted by law.
            </p>
            <p>
              Direct exchanges depend on inventory. When an exchange is not
              available, return the eligible item for a refund and place a new
              order for the preferred product.
            </p>
          </section>

          <section id="returns-issues" className="policy-page__section">
            <span className="policy-page__section-number">05</span>
            <h2>DAMAGE OR WRONG ITEM</h2>
            <p>
              Contact us within seven days of delivery if an order arrives damaged,
              incomplete, or different from your confirmation. Keep the product,
              shipping carton, and labels while the case is reviewed.
            </p>
            <p>Please include clear photographs of:</p>
            <ul>
              <li>the full product and the specific issue;</li>
              <li>the exterior and interior packaging;</li>
              <li>the shipping label; and</li>
              <li>the product serial or identification label, when present.</li>
            </ul>
            <p>
              Confirmed fulfillment errors or transit damage will be resolved with
              a replacement, repair, or refund as appropriate and subject to stock.
            </p>
          </section>

          <section id="returns-international" className="policy-page__section">
            <span className="policy-page__section-number">06</span>
            <h2>INTERNATIONAL RETURNS</h2>
            <p>
              International customers are responsible for return postage, export
              paperwork, and any carrier charges unless the item is confirmed
              damaged, defective, or incorrect. Mark the parcel as returned
              merchandise where permitted; we cannot reimburse duties or taxes
              collected by local authorities.
            </p>
            <p>
              Local consumer rights remain unaffected. For destination-specific
              guidance, contact <a href={`mailto:${siteConfig.contact.email}`}>{siteConfig.contact.email}</a> before shipping.
            </p>
          </section>

          <nav className="policy-page__related" aria-label="Related policies">
            <span>RELATED</span>
            <Link to="/shipping">SHIPPING</Link>
            <Link to="/privacy">PRIVACY</Link>
            <Link to="/terms">TERMS</Link>
          </nav>
        </article>
      </div>
    </main>
  );
}

export default Returns;
